import React, { useEffect, useCallback, useRef } from "react";
import debounce from "lodash/debounce";

const TableQuantityInput = ({ index, model, onBlurCommit, changedQuantity }) => {
    const inputRef = useRef(null);
    const latestRef = useRef({ index, model, onBlurCommit, changedQuantity });

    useEffect(() => {
        latestRef.current = { index, model, onBlurCommit, changedQuantity };
    });

    // Fallback: commit if user idles without blurring (e.g. navigates away via keyboard)
    const debouncedCommit = useRef(
        debounce(() => {
            const { index: i, model: m, onBlurCommit: obc, changedQuantity: cq } = latestRef.current;
            const value = inputRef.current ? Number(inputRef.current.value) : m.amount;
            cq(true);
            obc(m, i, value);
        }, 700)
    ).current;

    useEffect(() => () => debouncedCommit.cancel(), []);

    // Sync input value from parent only when not focused (e.g. external reset)
    useEffect(() => {
        if (inputRef.current && document.activeElement !== inputRef.current) {
            inputRef.current.value = String(model.amount ?? "");
        }
    }, [model.amount]);

    const onChange = useCallback((e) => {
        const next = e.target.value;
        if (next === "" || (/^\d{1,4}$/.test(next) && Number(next) <= 9999)) {
            debouncedCommit(Number(next));
        }
    }, []);

    const onBlur = useCallback(() => {
        debouncedCommit.cancel();
        const { index: i, model: m, onBlurCommit: obc, changedQuantity: cq } = latestRef.current;
        const value = inputRef.current ? Number(inputRef.current.value) : m.amount;
        if (value > 0 && value !== m.amount) {
            cq(true);
            obc(m, i, value);
        }
    }, []);

    return (
        <input
            ref={inputRef}
            id={`amount-${index}`}
            type="number"
            defaultValue={model.amount}
            min={1}
            max={9999}
            onChange={onChange}
            onBlur={onBlur}
        />
    );
};

const areEqual = (prevProps, nextProps) =>
    prevProps.model.id === nextProps.model.id && prevProps.model.amount === nextProps.model.amount;

export default React.memo(TableQuantityInput, areEqual);
